import { inject, Injectable } from '@angular/core';
import { IMenuItem } from '../models/menu-item.model';
import { environment } from 'src/environments/environment';
import { IMasterPage } from '../models/master-page.model';
import { HttpClient } from '@angular/common/http';
import { IFooterLink } from '../models/footer-link.model';
import { IFooterLogo } from '../models/footer-logo.model';
import { BehaviorSubject, Observable } from 'rxjs';
import { IAboutPage } from '../models/about-page.model';
import { IContributorsPage } from '../models/contributors-page.model';
import { IFixedPage } from '../models/fixed-page.model';
import { IReadingAreaPage } from '../models/reading-area-page.model';
import { IPublicationsPage } from '../models/publications-page.model';
import { IMediaPage } from '../models/media-page.model';
import { ISeedArchivePage } from '../models/seed-archive-page.model';
import { IGlossaryPage } from '../models/glossary-page.model';
import { IActivitiesPage } from '../models/activities-page.model';
import { IHomePage } from '../models/home-page.model';
import { IPostItemPage } from '../models/post-item-page.model';


@Injectable({ providedIn: 'root' })
export class SitePageService {
    private http = inject(HttpClient);

    private loadingMasterPage : boolean = false;
    private masterPage : BehaviorSubject<IMasterPage | null> = new BehaviorSubject<IMasterPage | null>(null);
    private menuItems : BehaviorSubject<IMenuItem[]> = new BehaviorSubject<IMenuItem[]>(!!this.masterPage && this.masterPage.value ? this.masterPage.value.menuItems : []);
    private footerLinks : BehaviorSubject<IFooterLink[]> = new BehaviorSubject<IFooterLink[]>(!!this.masterPage && this.masterPage.value ? this.masterPage.value.footerLinks : []);
    private institutionalLogos : BehaviorSubject<IFooterLogo[]> = new BehaviorSubject<IFooterLogo[]>(!!this.masterPage && this.masterPage.value ? this.masterPage.value.institutionalLogos : []);
    private partnersLogos : BehaviorSubject<IFooterLogo[]> = new BehaviorSubject<IFooterLogo[]>(!!this.masterPage && this.masterPage.value ? this.masterPage.value.partners : []);

    private apiBaseUrl = environment.apiBaseUrl;

    getMenus(): Observable<IMenuItem[]> {
        if(this.masterPage.value == null && !this.loadingMasterPage){
            this.getMasterPage();
        }

        return this.menuItems.asObservable();
    }

    getFooterLinks(): Observable<IFooterLink[]> {
        if(this.masterPage.value == null && !this.loadingMasterPage){
            this.getMasterPage();
        }

        return this.footerLinks.asObservable();
    }

    getInstitutionalLogos(): Observable<IFooterLogo[]> {
        if(this.masterPage.value == null && !this.loadingMasterPage){
            this.getMasterPage();
        }

        return this.institutionalLogos.asObservable();
    }

    getPartnersLogos(): Observable<IFooterLogo[]> {
        if(this.masterPage.value == null && !this.loadingMasterPage){
            this.getMasterPage();
        }
        return this.partnersLogos.asObservable();
    }


    getMasterPage() : void {
      this.loadingMasterPage = true;
      this.http.get<IMasterPage>(this.apiBaseUrl + 'Public/MasterPage')
        .subscribe({
            next: (mp) => {
              this.masterPage.next(mp);
              this.menuItems.next(mp.menuItems);
              this.footerLinks.next(mp.footerLinks);
              this.institutionalLogos.next(mp.institutionalLogos);
              this.partnersLogos.next(mp.partners);
              this.loadingMasterPage = false;
            }
        });
    }

    getFixedPage(permalink: string) : Observable<IFixedPage> {
        return this.http.get<IFixedPage>(this.apiBaseUrl + 'Public/FixedPage/' + permalink);
    }

    getHomePage() : Observable<IHomePage> {
        return this.http.get<IHomePage>(this.apiBaseUrl + 'Public/HomePage');
    }

    getActivitiesPage() : Observable<IActivitiesPage> {
        return this.http.get<IActivitiesPage>(this.apiBaseUrl + 'Public/Activities');
    }

    getPostPage(permalink: string) : Observable<IPostItemPage> {
        return this.http.get<IPostItemPage>(this.apiBaseUrl + 'Public/Posts/' + permalink);
    }

    getGlossaryPage() : Observable<IGlossaryPage> {
        return this.http.get<IGlossaryPage>(this.apiBaseUrl + 'Public/Glossary');
    }

    getSeedArchivePage() : Observable<ISeedArchivePage> {
        return this.http.get<ISeedArchivePage>(this.apiBaseUrl + 'Public/SeedArchive');
    }

    getMediaPage() : Observable<IMediaPage> {
        return this.http.get<IMediaPage>(this.apiBaseUrl + 'Public/Media');
    }

    getPublicationsPage() : Observable<IPublicationsPage> {
        return this.http.get<IPublicationsPage>(this.apiBaseUrl + 'Public/Publications');
    }

    getPublicationPage(permalink: string) : Observable<IFixedPage> {
        return this.http.get<IFixedPage>(this.apiBaseUrl + 'Public/Publications/' + permalink);
    }

    getReadingAreaPage() : Observable<IReadingAreaPage> {
        return this.http.get<IReadingAreaPage>(this.apiBaseUrl + 'Public/ReadingArea');
    }

    getContributorsPage() : Observable<IContributorsPage> {
        return this.http.get<IContributorsPage>(this.apiBaseUrl + 'Public/Contributors');
    }

    getAboutPage() : Observable<IAboutPage> {
        return this.http.get<IAboutPage>(this.apiBaseUrl + 'Public/About');
    }
}
